import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { proxies } from "../services/RepositoryService";
import { post } from "../services/HttpService";
import StarekContext from "../context/StarekContext";
import PositiveSSLSeal from "../components/PositiveSSLSeal";

function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const starekContext = useContext(StarekContext);

    const token = searchParams.get("token") || "";
    const workspaceParam = searchParams.get("workspace") || "";

    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);
    const [tokenInvalid, setTokenInvalid] = useState(false);

    // Validate that we have the required query params on mount
    useEffect(() => {
        if (!token || !workspaceParam) {
            setTokenInvalid(true);
            setError(
                "This reset link is missing required parameters. Please request a new one.",
            );
        }
    }, [token, workspaceParam]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!newPassword.trim() || newPassword.trim().length < 8) {
            setError("Password must be at least 8 characters.");
            return;
        }
        if (newPassword !== confirmPassword) {
            setError("Passwords do not match.");
            return;
        }

        setLoading(true);
        try {
            const foundWorkspace = await proxies(workspaceParam);

            const url = `${foundWorkspace.engine.scheme}://${foundWorkspace.engine.host}:${foundWorkspace.engine.port}${process.env.REACT_APP_API_PREFIX}/${foundWorkspace.workspace_id}/auth/reset-password`;

            await post(url, {
                token,
                newPassword: newPassword.trim(),
            });

            setSuccess(true);

            // Redirect to login after 3 seconds with a success message
            setTimeout(() => {
                starekContext?.showSuccess?.(
                    "Password updated successfully. Please sign in.",
                );
                navigate("/login");
            }, 3000);
        } catch (err) {
            console.error("[ResetPassword] error:", err);
            // HttpError carries the real status now; the old check matched
            // "400"/"401" inside the message string.
            const msg =
                err?.status === 400 || err?.status === 401
                    ? "This reset link has expired or has already been used. Please request a new one."
                    : "Something went wrong. Please try again or request a new reset link.";
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    // ── Invalid / missing token ──
    if (tokenInvalid) {
        return (
            <div className="login-page">
                <div className="login-box">
                    <div
                        className="login-logo d-flex flex-column align-items-center justify-content-center"
                        style={{ marginBottom: "1rem" }}
                    >
                        <img
                            src="/dist/img/light-starek-brand-512x512.png"
                            alt="Starek"
                            className="brand-image elevation-3"
                            style={{ opacity: "0.8" }}
                        />
                    </div>
                    <div className="card">
                        <div className="card-body login-card-body text-center py-4">
                            <div style={{ fontSize: "3rem" }}>⚠️</div>
                            <h5 className="mt-3 mb-2">Invalid Reset Link</h5>
                            <p className="text-muted" style={{ fontSize: "14px" }}>
                                {error}
                            </p>
                            <Link
                                to="/forgot-password"
                                className="btn btn-primary btn-sm mt-2"
                            >
                                Request a New Link
                            </Link>
                        </div>
                    </div>
                    <PositiveSSLSeal />
                </div>
            </div>
        );
    }

    // ── Success state ──
    if (success) {
        return (
            <div className="login-page">
                <div className="login-box">
                    <div
                        className="login-logo d-flex flex-column align-items-center justify-content-center"
                        style={{ marginBottom: "1rem" }}
                    >
                        <img
                            src="/dist/img/light-starek-brand-512x512.png"
                            alt="Starek"
                            className="brand-image elevation-3"
                            style={{ opacity: "0.8" }}
                        />
                    </div>
                    <div className="card">
                        <div className="card-body login-card-body text-center py-4">
                            <div style={{ fontSize: "3rem" }}>✅</div>
                            <h5 className="mt-3 mb-2">Password Updated</h5>
                            <p className="text-muted" style={{ fontSize: "14px" }}>
                                Your password has been changed successfully.
                                Redirecting you to sign in…
                            </p>
                            <Link
                                to="/login"
                                className="btn btn-primary btn-sm mt-2"
                            >
                                Sign In Now
                            </Link>
                        </div>
                    </div>
                    <PositiveSSLSeal />
                </div>
            </div>
        );
    }

    // ── Form state ──
    return (
        <div className="login-page">
            <div className="login-box">
                <div
                    className="login-logo d-flex flex-column align-items-center justify-content-center"
                    style={{ marginBottom: "1rem" }}
                >
                    <img
                        src="/dist/img/light-starek-brand-512x512.png"
                        alt="Starek"
                        className="brand-image elevation-3"
                        style={{ opacity: "0.8" }}
                    />
                </div>

                <div className="card">
                    <div className="card-body login-card-body">
                        <p className="login-box-msg mb-3">
                            Choose a new password for your account on workspace{" "}
                            <strong>{workspaceParam}</strong>.
                        </p>

                        {error && (
                            <div
                                className="alert alert-danger py-2 px-3 mb-3"
                                style={{ fontSize: "14px" }}
                            >
                                {error}{" "}
                                {(error.includes("expired") ||
                                    error.includes("used")) && (
                                    <Link
                                        to="/forgot-password"
                                        className="alert-link"
                                    >
                                        Request a new link
                                    </Link>
                                )}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} noValidate>
                            {/* New Password */}
                            <div className="input-group mb-3">
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder="New Password (min. 8 characters)"
                                    value={newPassword}
                                    onChange={(e) =>
                                        setNewPassword(e.target.value)
                                    }
                                    autoComplete="new-password"
                                    required
                                    minLength={8}
                                />
                                <div className="input-group-append">
                                    <div className="input-group-text">
                                        <span className="fas fa-lock" />
                                    </div>
                                </div>
                            </div>

                            {/* Confirm Password */}
                            <div className="input-group mb-3">
                                <input
                                    type="password"
                                    className="form-control"
                                    placeholder="Confirm New Password"
                                    value={confirmPassword}
                                    onChange={(e) =>
                                        setConfirmPassword(e.target.value)
                                    }
                                    autoComplete="new-password"
                                    required
                                />
                                <div className="input-group-append">
                                    <div className="input-group-text">
                                        <span
                                            className={
                                                confirmPassword &&
                                                confirmPassword !== newPassword
                                                    ? "fas fa-times text-danger"
                                                    : confirmPassword &&
                                                        confirmPassword ===
                                                            newPassword
                                                      ? "fas fa-check text-success"
                                                      : "fas fa-lock"
                                            }
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-6">
                                    <Link
                                        to="/login"
                                        className="btn btn-outline-secondary btn-block"
                                    >
                                        Cancel
                                    </Link>
                                </div>
                                <div className="col-6">
                                    <button
                                        type="submit"
                                        className="btn btn-primary btn-block"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-1"
                                                    role="status"
                                                    aria-hidden="true"
                                                />
                                                Saving…
                                            </>
                                        ) : (
                                            "Set New Password"
                                        )}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="text-white" style={{ textAlign: "center" }}>
                    <br />
                    Structured Telemetry and Reporting
                </div>
                <PositiveSSLSeal />
            </div>
        </div>
    );
}

export default ResetPassword;
